import {
  verifyBeforeUpdateEmail,
  sendEmailVerification,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { firebaseAuth } from "@/helpers/firebase/firebase";

/**
 * Updates the current user's email address with verification
 * Sends a verification email first, only updates after user verifies
 * @param newEmail The new email address
 * @throws Error if user is not authenticated or needs re-authentication
 */
export async function updateUserEmail(newEmail: string): Promise<void> {
  const user = firebaseAuth.currentUser;

  if (!user) {
    throw new Error("No authenticated user found");
  }

  // With email enumeration protection enabled, we must verify before updating
  await verifyBeforeUpdateEmail(user, newEmail);
}

/**
 * Re-authenticates the current user with their password
 * Required before performing sensitive operations like email changes
 * @param password The user's current password
 * @throws Error if user is not authenticated or password is incorrect
 */
export async function reauthenticateUser(password: string): Promise<void> {
  const user = firebaseAuth.currentUser;

  if (!user || !user.email) {
    throw new Error("No authenticated user found");
  }

  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
}

/**
 * Sends a verification email to the current user
 * @throws Error if user is not authenticated
 */
export async function sendVerificationEmail(): Promise<void> {
  const user = firebaseAuth.currentUser;

  if (!user) {
    throw new Error("No authenticated user found");
  }

  await sendEmailVerification(user);
}

/**
 * Attempts to change the user's email with optional re-authentication
 * @param newEmail The new email address
 * @param password Optional password for re-authentication (required if previous attempt failed with requires-recent-login)
 * @throws AuthError - Firebase auth errors bubble up to caller
 */
export async function changeUserEmail(
  newEmail: string,
  password?: string,
): Promise<void> {
  const user = firebaseAuth.currentUser;

  if (!user) {
    throw new Error("No authenticated user found");
  }

  // If password is provided, re-authenticate first
  if (password && user.email) {
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  }

  // Update the email
  await verifyBeforeUpdateEmail(user, newEmail);
}

/**
 * Checks if the current user has password authentication enabled
 * @returns true if user has password auth provider
 */
export function hasPasswordAuth(): boolean {
  const user = firebaseAuth.currentUser;
  if (!user?.providerData) {
    return false;
  }

  return user.providerData.some(
    (provider) => provider?.providerId === "password",
  );
}
